import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Truck, MapPin, ShieldCheck, Star, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, img, money, fmtErr } from "@/lib/api";
import { track } from "@/lib/track";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, SaveButton } from "@/components/site/ProductCard";
import { ClaimDialog } from "@/components/site/ClaimDialog";
import { EASE } from "@/components/site/Reveal";

const Gallery = ({ p }) => {
  const [i, setI] = useState(0);
  const imgs = p.images?.length ? p.images : [""];
  return (
    <div>
      <div className="chamfer relative aspect-[4/5] overflow-hidden bg-[#110720]">
        <motion.img key={i} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: EASE }} src={img(imgs[i])} alt={p.title} className="h-full w-full object-cover" data-testid="product-main-image" />
        <div className="absolute left-5 top-5"><StatusBadge status={p.status} /></div>
      </div>
      {imgs.length > 1 && (
        <div className="mt-4 flex gap-3">
          {imgs.map((s, k) => (
            <button key={s + k} onClick={() => setI(k)} data-testid={`product-thumb-${k}`} className={`h-20 w-16 overflow-hidden border transition-colors ${k === i ? "border-[#a855f7]" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={img(s)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Spec = ({ k, v }) => v ? (
  <div className="flex justify-between border-b border-[#281245] py-4 text-sm">
    <span className="text-[#a78bce]">{k}</span><span className="text-[#f3eefc]">{v}</span>
  </div>
) : null;

const Stars = ({ value, size = "h-4 w-4" }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-[#a855f7] text-[#a855f7]" : "text-[#3b1d63]"}`} />
    ))}
  </div>
);

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; }
};

const ReviewForm = ({ pid, onDone }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setPhoto(data?.url || "");
    } catch (er) {
      toast.error(fmtErr(er));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please choose a star rating");
    if (!text.trim()) return toast.error("Please write a few words about your item");
    setBusy(true);
    try {
      await api.post(`/products/${pid}/reviews`, { rating, text: text.trim(), photos: photo ? [photo] : [] });
      track("review_posted", { product_id: pid });
      toast.success("Thank you — your review is live");
      onDone();
    } catch (er) {
      toast.error(fmtErr(er));
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <form onSubmit={submit} className="chamfer border border-[#281245] bg-[#0b0514] p-6 md:p-8" data-testid="review-form">
      <p className="font-display text-2xl text-[#f3eefc]">Write a review</p>
      <p className="mt-1 text-sm text-[#a78bce]">Tell others about the item you received.</p>
      <div className="mt-6 flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} data-testid={`review-star-${n}`} aria-label={`${n} star${n > 1 ? "s" : ""}`} className="p-1">
            <Star className={`h-8 w-8 transition-colors ${n <= (hover || rating) ? "fill-[#a855f7] text-[#a855f7]" : "text-[#3b1d63]"}`} />
          </button>
        ))}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={4} placeholder="How was the fit, quality, condition?" data-testid="review-text-input" className="mt-5 w-full resize-none rounded-xl border border-[#281245] bg-[#07030c] p-4 text-sm text-[#f3eefc] placeholder:text-[#6b5a87] focus:border-[#7c3aed] focus:outline-none" />
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/gif" onChange={pick} className="hidden" data-testid="review-photo-input" />
        {photo ? (
          <div className="relative h-20 w-20 overflow-hidden rounded-lg border border-[#3b1d63]" data-testid="review-photo-preview">
            <img src={img(photo)} alt="Your review" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setPhoto("")} data-testid="review-photo-remove" aria-label="Remove photo" className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/70 text-white"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="review-photo-button" className="inline-flex items-center gap-2 rounded-full border border-[#3b1d63] px-4 py-2.5 text-sm text-[#cbbbe6] transition-colors hover:border-[#7c3aed] hover:text-white disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {uploading ? "Uploading…" : "Add a photo (optional)"}
          </button>
        )}
        <button type="submit" disabled={busy || uploading} data-testid="review-submit-button" className="ml-auto rounded-full bg-[#7c3aed] px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-[#a855f7] disabled:opacity-60">
          {busy ? "Posting…" : "Post review"}
        </button>
      </div>
    </form>
  );
};

const Reviews = ({ pid, user }) => {
  const [data, setData] = useState({ reviews: [], can_review: false });
  const load = useCallback(() => {
    api.get(`/products/${pid}/reviews`).then((r) => setData({ reviews: r.data?.reviews ?? [], can_review: !!r.data?.can_review })).catch(() => {});
  }, [pid]);
  useEffect(() => { load(); }, [load, user?.id]);

  const { reviews, can_review: canReview } = data;
  const avg = reviews.length ? reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length : 0;

  return (
    <section className="mt-24 border-t border-[#281245] pt-16" data-testid="product-reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <h2 className="font-display text-4xl text-[#f3eefc] md:text-5xl">Reviews</h2>
        <div className="flex items-center gap-3" data-testid="review-summary">
          <Stars value={avg} size="h-5 w-5" />
          <span className="font-mono-j text-sm text-[#cbbbe6]">{reviews.length ? `${avg.toFixed(1)} · ${reviews.length} review${reviews.length > 1 ? "s" : ""}` : "No reviews yet"}</span>
        </div>
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <div className={`space-y-4 ${canReview ? "lg:col-span-7" : "lg:col-span-12"}`}>
          {reviews.length === 0 && <p className="text-sm text-[#a78bce]">Reviews are written by customers who received this item.</p>}
          {reviews.map((r) => (
            <article key={r.id} className="chamfer border border-[#281245] bg-[#0b0514] p-6" data-testid="review-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><p className="text-sm font-medium text-[#f3eefc]">{r.name}</p><p className="font-mono-j text-xs text-[#a78bce]">{fmtDate(r.created_at)}</p></div>
                <Stars value={r.rating} />
              </div>
              <p className="mt-4 whitespace-pre-line leading-relaxed text-[#cbbbe6]">{r.text}</p>
              {r.photos?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {r.photos.map((p, k) => (
                    <a key={k} href={img(p)} target="_blank" rel="noreferrer" className="block h-28 w-28 overflow-hidden rounded-lg border border-[#3b1d63]" data-testid={`review-photo-${k}`}>
                      <img src={img(p)} alt="Customer photo" className="h-full w-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
        {canReview && <div className="lg:col-span-5"><ReviewForm pid={pid} onDone={load} /></div>}
      </div>
    </section>
  );
};

export default function Product() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true));
    track("product_view", { product_id: id });
  }, [id]);

  if (err) return <div className="grid min-h-[70vh] place-items-center pt-24 text-center"><div><p className="font-display text-4xl">Item not found</p><Link to="/shop" className="mt-6 inline-block text-[#d8b4fe]">Back to shop</Link></div></div>;
  if (!p) return <div className="min-h-[80vh]" />;

  const claim = () => {
    if (!user) return nav(`/login?next=/item/${id}`);
    setOpen(true);
    return undefined;
  };
  const sold = p.status === "Sold";

  return (
    <div className="mx-auto max-w-[1400px] px-5 pb-32 pt-28 md:px-10 md:pt-32" data-testid="product-page">
      <Link to="/shop" className="mb-10 inline-flex items-center gap-2 text-sm text-[#cbbbe6] hover:text-white" data-testid="product-back-link"><ArrowLeft className="h-4 w-4" /> Back to shop</Link>
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-7"><Gallery p={p} /></div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }} className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
          <h1 className="font-display text-4xl leading-[1] text-[#f3eefc] sm:text-5xl lg:text-6xl" data-testid="product-title">{p.title}</h1>
          <p className="font-mono-j mt-6 text-3xl text-[#f3eefc]" data-testid="product-price">{money(p.price)} <span className="text-sm text-[#a78bce]">CAD</span></p>
          {p.description && <p className="mt-8 leading-relaxed text-[#cbbbe6]">{p.description}</p>}
          <div className="mt-8"><Spec k="Size" v={p.size} /><Spec k="Condition" v={p.condition} /><Spec k="Category" v={p.category} /></div>
          <div className="mt-10 flex items-center gap-3">
            <button onClick={claim} disabled={sold} data-testid="product-claim-button" className="flex-1 rounded-full bg-[#7c3aed] py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#a855f7] disabled:cursor-not-allowed disabled:bg-[#281245] disabled:text-[#a78bce]">
              {sold ? "Sold" : p.status === "Reserved" ? "Reserved — join the waitlist" : "Claim this item"}
            </button>
            <SaveButton pid={p.id} className="h-[52px] w-[52px]" />
          </div>
          <ul className="mt-10 space-y-3 text-sm text-[#cbbbe6]">
            <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-[#a855f7]" /> No payment now — we confirm with you first</li>
            <li className="flex items-center gap-3"><Truck className="h-4 w-4 text-[#a855f7]" /> Ships across Canada</li>
            <li className="flex items-center gap-3"><MapPin className="h-4 w-4 text-[#a855f7]" /> From BC, Canada</li>
          </ul>
        </motion.div>
      </div>
      <Reviews pid={p.id} user={user} />
      {user && <ClaimDialog open={open} onOpenChange={setOpen} product={p} />}
    </div>
  );
}
